import { ChangeDetectionStrategy, Component } from '@angular/core';
import {
  IOptionMenuItem,
  PhoenixButtonModule,
  PhoenixMenuButtonModule,
} from '@kehe/phoenix-button';
import { PhoenixEmptyStateModule } from '@kehe/phoenix-empty-state';
import { PhoenixIndicatorsModule } from '@kehe/phoenix-indicators';
import {
  PhoenixAlertsModule,
  PhoenixToastService,
} from '@kehe/phoenix-notifications';
import { GridModule, PageChangeEvent } from '@progress/kendo-angular-grid';
import { PhoenixModalComponent } from '@kehe/phoenix-modal';
import { TransactionTypeListStore } from './transaction-type-grid.store';
import { CommonModule } from '@angular/common';
import { KeheUtilDirectivesModule } from '@kehe/phoenix-utils';
import { PhoenixSearchModule } from '@kehe/phoenix-search';
import { Destroyable } from '@app/abstract/destroyable';
import { ReactiveFormsModule } from '@angular/forms';
import { PagerModule } from '@progress/kendo-angular-pager';
import { SortDescriptor } from '@progress/kendo-data-query';
import { AddTransactionTypeModalComponent } from '../../components/add-transaction-type-modal/add-transaction-type-modal.component';

@Component({
  selector: 'app-transaction-type-grid',
  imports: [
    CommonModule,
    GridModule,
    PagerModule,
    PhoenixAlertsModule,
    PhoenixEmptyStateModule,
    PhoenixIndicatorsModule,
    PhoenixButtonModule,
    KeheUtilDirectivesModule,
    PhoenixMenuButtonModule,
    ReactiveFormsModule,
    PhoenixSearchModule,
    PhoenixModalComponent,
    AddTransactionTypeModalComponent,
  ],
  templateUrl: './transaction-type-grid.component.html',
  styleUrl: './transaction-type-grid.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [TransactionTypeListStore],
})
export class TransactionTypeGridComponent extends Destroyable {
  addEditModalOpen = false;
  isEditMode = false;
  editRow: any | null = null;
  deactivateModalOpen = false;
  reactivateModalOpen = false;
  showReactivateWarning = false;
  selectedRow: any | null = null;
  form = this._componentStore.form;
  vm$ = this._componentStore.vm$;

  defaultEllipsesMenuOptions: IOptionMenuItem[] = [
    {
      name: 'Edit',
      data: 'Edit',
      enabled: true,
      automationId: 'transaction-type-table-ellipsis-edit-option',
    },
    {
      name: 'Download Template',
      data: 'Download Template',
      enabled: true,
      automationId: 'transaction-type-table-ellipsis-download-template-option',
    },
    {
      name: 'Download Supporting Document',
      data: 'Download Supporting Document',
      enabled: true,
      automationId:
        'transaction-type-table-ellipsis-download-supporting-document-option',
    },
    {
      name: 'Deactivate',
      data: 'Deactivate',
      enabled: true,
      automationId: 'transaction-type-table-ellipsis-deactivate-option',
    },
  ];

  get selectedTransactionTypeLabel(): string {
    const name = this.selectedRow?.codeValue ?? this.selectedRow?.name ?? '--';
    const prefix =
      this.selectedRow?.prefix ?? this.selectedRow?.transactionPrefix ?? '';
    return prefix ? `${name} ${prefix}` : name;
  }

  constructor(
    public _componentStore: TransactionTypeListStore,
    private toast: PhoenixToastService,
  ) {
    super();
  }

  onEllipsisOptionSelected(option: any, dataItem: any) {
    const selected = option?.data;

    if (selected === 'Deactivate') {
      this.selectedRow = dataItem;
      this.deactivateModalOpen = true;
      return;
    }

    if (selected === 'Download Template') {
      this._componentStore.downloadTemplate(dataItem.code);
      return;
    }

    if (selected === 'Download Supporting Document') {
      this._componentStore.downloadSupportingDocument(dataItem.code);
      return;
    }

    if (selected === 'Reactivate') {
      const code = dataItem?.code;
      if (!code) return;

      this.reactivateModalOpen = true;
      this.selectedRow = dataItem;

      if (dataItem.hasSupportingDocument && dataItem.s3KeySupportingDocument) {
        this.showReactivateWarning = true;
      } else {
        this.showReactivateWarning = false;
      }

      return;
    }

    if (selected === 'Edit') {
      this.openEditModal(dataItem);
      return;
    }
  }

  onTemplateDownload(dataItem: any): void {
    this._componentStore.downloadTemplate(dataItem.code);
  }

  closeReactivateModal() {
    this.reactivateModalOpen = false;
    this.selectedRow = null;
  }

  closeDeactivateModal() {
    this.deactivateModalOpen = false;
    this.selectedRow = null;
  }

  confirmDeactivate() {
    const code = this.selectedRow?.code;
    if (!code) {
      this.closeDeactivateModal();
      return;
    }

    this._componentStore.deactivateTransactionType({
      code,
      onSuccess: () => {
        this.toast.showSuccessToast('Transaction Type removed successfully!');
        this.closeDeactivateModal();
      },
    });
  }

  confirmReactivate() {
    const code = this.selectedRow?.code;
    if (!code) {
      this.closeReactivateModal();
      return;
    }

    this._componentStore.reactivateTransactionType({
      code,
      onSuccess: () => {
        this.toast.showSuccessToast(
          'Transaction Type reactivated successfully!',
        );
        this.closeReactivateModal();
      },
    });
  }

  public handlePageChange(e: PageChangeEvent): void {
    this._componentStore.handlePageChange(e);
  }

  onPageChange(event: PageChangeEvent) {
    window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
    this._componentStore.updateGridConfigPagination(event);
  }

  sortChange(sortEvent: SortDescriptor[]) {
    this._componentStore.updateGridConfigSort(sortEvent[0]);
  }

  openAddModal() {
    this.isEditMode = false;
    this.editRow = null;
    this.addEditModalOpen = true;
  }

  openEditModal(dataItem: any) {
    this.isEditMode = true;
    this.editRow = dataItem;
    this.addEditModalOpen = true;
  }

  closeAddEditModal() {
    this.addEditModalOpen = false;
    this.isEditMode = false;
    this.editRow = null;
  }

  get modalText(): string {
    return `
        You are reactivating a transaction type that had supporting educational document(s). Please edit the transaction type and ensure that the document is up to date before reactivation!
  `;
  }
}
